import React, { useEffect, useRef, useState } from 'react';
import { CheckboxItem, Text, Tooltip } from '..';
import { colorSet } from '@theme';
import { x_black } from '@assets/icons';
import { IOptionList, IOptionListItem } from '@types';
import { Box } from '@mui/material';
import { ChipPaperType, styleConstant, StandardFacialExpressionChildOptionValueEnum } from '@constant';
import {
  FaceSatisfiedIcon,
  FaceAffectionateIcon,
  FaceCoquettishIcon,
  FaceJoyIcon,
  FaceNeutralIcon,
  FaceSurprisedIcon,
  FacePensiveIcon,
  FaceDisgustIcon,
  FaceAngryIcon,
  FaceEmbarrassedIcon,
  FaceFearIcon,
  FaceSadnessIcon,
  FaceEerieIcon,
  FaceCautiousIcon,
  FaceFrustrationIcon,
  FaceShamelessIcon,
  FaceShameIcon,
  FaceSuspicionIcon,
  FaceAnxietyIcon,
  FaceScreamIcon,
  FaceContemptIcon
} from '@assets/iconComponents';

interface IPaperSelectProps {
  options: IOptionList;
  selectedOptions: IOptionListItem[];
  onSelect: (option: IOptionListItem) => void;
  paperType: ChipPaperType;
  onFacialExpressionCategoryClick: (category: 'positive' | 'neutral' | 'negative', isCategoryChecked: boolean) => void;
}

// 표정 카테고리 정의
const FACE_CATEGORIES = {
  positive: {
    label: '긍정',
    options: {
      만족: {
        name: '만족',
        value: StandardFacialExpressionChildOptionValueEnum['만족'],
        isSelected: false
      },
      자애: {
        name: '자애',
        value: StandardFacialExpressionChildOptionValueEnum['자애'],
        isSelected: false
      },
      새침: {
        name: '새침',
        value: StandardFacialExpressionChildOptionValueEnum['새침'],
        isSelected: false
      },
      기쁨: {
        name: '기쁨',
        value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
        isSelected: false
      }
    }
  },
  neutral: {
    label: '중립',
    options: {
      무표정: {
        name: '무표정',
        value: StandardFacialExpressionChildOptionValueEnum['무표정'],
        isSelected: false
      },
      놀람: {
        name: '놀람',
        value: StandardFacialExpressionChildOptionValueEnum['놀람'],
        isSelected: false
      },
      고민: {
        name: '고민',
        value: StandardFacialExpressionChildOptionValueEnum['고민'],
        isSelected: false
      }
    }
  },
  negative: {
    label: '부정',
    options: {
      혐오: {
        name: '혐오',
        value: StandardFacialExpressionChildOptionValueEnum['혐오'],
        isSelected: false
      },
      분노: {
        name: '분노',
        value: StandardFacialExpressionChildOptionValueEnum['분노'],
        isSelected: false
      },
      당황: {
        name: '당황',
        value: StandardFacialExpressionChildOptionValueEnum['당황'],
        isSelected: false
      },
      공포: {
        name: '공포',
        value: StandardFacialExpressionChildOptionValueEnum['공포'],
        isSelected: false
      },
      슬픔: {
        name: '슬픔',
        value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
        isSelected: false
      },
      섬뜩: {
        name: '섬뜩',
        value: StandardFacialExpressionChildOptionValueEnum['섬뜩'],
        isSelected: false
      },
      경계: {
        name: '경계',
        value: StandardFacialExpressionChildOptionValueEnum['경계'],
        isSelected: false
      },
      좌절: {
        name: '좌절',
        value: StandardFacialExpressionChildOptionValueEnum['좌절'],
        isSelected: false
      },
      뻔뻔: {
        name: '뻔뻔',
        value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
        isSelected: false
      },
      수치: {
        name: '수치',
        value: StandardFacialExpressionChildOptionValueEnum['수치'],
        isSelected: false
      },
      의심: {
        name: '의심',
        value: StandardFacialExpressionChildOptionValueEnum['의심'],
        isSelected: false
      },
      불안: {
        name: '불안',
        value: StandardFacialExpressionChildOptionValueEnum['불안'],
        isSelected: false
      },
      절규: {
        name: '절규',
        value: StandardFacialExpressionChildOptionValueEnum['절규'],
        isSelected: false
      },
      경멸: {
        name: '경멸',
        value: StandardFacialExpressionChildOptionValueEnum['경멸'],
        isSelected: false
      }
    }
  }
};

// 표정 옵션별 아이콘 매핑
const FACE_ICON_MAP = {
  만족: FaceSatisfiedIcon,
  자애: FaceAffectionateIcon,
  새침: FaceCoquettishIcon,
  기쁨: FaceJoyIcon,
  무표정: FaceNeutralIcon,
  놀람: FaceSurprisedIcon,
  고민: FacePensiveIcon,
  혐오: FaceDisgustIcon,
  분노: FaceAngryIcon,
  당황: FaceEmbarrassedIcon,
  공포: FaceFearIcon,
  슬픔: FaceSadnessIcon,
  섬뜩: FaceEerieIcon,
  경계: FaceCautiousIcon,
  좌절: FaceFrustrationIcon,
  뻔뻔: FaceShamelessIcon,
  수치: FaceShameIcon,
  의심: FaceSuspicionIcon,
  불안: FaceAnxietyIcon,
  절규: FaceScreamIcon,
  경멸: FaceContemptIcon
} as const;

const PaperSelect: React.FC<IPaperSelectProps> = (props: IPaperSelectProps) => {
  const { options, selectedOptions, onSelect, paperType, onFacialExpressionCategoryClick } = props;

  const paperSelectRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'left' | 'right'>('left');

  useEffect(() => {
    if (paperSelectRef.current) {
      const rect = paperSelectRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // PaperSelect가 화면 오른쪽으로 넘어가는지 확인
      if (rect.right > viewportWidth) {
        setPosition('right');
      } else {
        setPosition('left');
      }
    }
  }, [paperType, options]);

  return (
    <div
      ref={paperSelectRef}
      style={{
        position: 'absolute',
        top: 48,
        [position]: 0,
        zIndex: styleConstant.zIndexChipPaper,
        padding: '6px 0px',
        backgroundColor: colorSet.white,
        boxShadow: '0px 5px 25px rgba(0, 0, 0, 0.1)'
      }}
    >
      {paperType === ChipPaperType.SELECT ? (
        <>
          {Object.keys(options).map((optionKey, index) => (
            <Box
              key={index}
              onClick={(event) => {
                event.stopPropagation(); // 이벤트 전파 방지
                onSelect(options[optionKey]);
              }}
              sx={{
                width: 120,
                padding: '6px 20px',
                cursor: 'pointer',
                backgroundColor: selectedOptions.some((opt) => opt.value === options[optionKey].value)
                  ? 'rgba(119, 78, 255, 0.15)'
                  : colorSet.white,
                ':hover': {
                  backgroundColor: selectedOptions.some((opt) => opt.value === options[optionKey].value)
                    ? 'rgba(119, 78, 255, 0.15)'
                    : colorSet.gray100,
                  '& .optionListItemText': {
                    fontWeight: 'bold'
                  }
                }
              }}
            >
              <Text className="optionListItemText" variant="body1">
                {optionKey}
              </Text>
            </Box>
          ))}
        </>
      ) : null}

      {paperType === ChipPaperType.CHECKBOX_LIST ? (
        <>
          {Object.keys(options).map((optionKey, index) => (
            <div
              key={index}
              onClick={(event) => event.stopPropagation()} // 이벤트 전파 방지
              style={{ width: 148, padding: '6px 20px' }}
            >
              <CheckboxItem
                label={optionKey}
                isChecked={selectedOptions.some((opt) => opt.value === options[optionKey].value)}
                onChange={() => {
                  onSelect(options[optionKey]);
                }}
              />
            </div>
          ))}
        </>
      ) : null}

      {paperType === ChipPaperType.FACE_CHECKBOX_LIST ? (
        <div
          onClick={(event) => event.stopPropagation()} // 이벤트 전파 방지
          style={{
            display: 'flex',
            width: 444,
            padding: '6px 0px'
          }}
        >
          {/* 긍정 카테고리 */}
          <div style={{ flex: 1 }}>
            <div style={{ padding: '6px 20px', borderBottom: `1px solid ${colorSet.gray200}` }}>
              <CheckboxItem
                label="긍정"
                isChecked={Object.values(FACE_CATEGORIES.positive.options).every((opt) =>
                  selectedOptions.some((selected) => selected.value === opt.value)
                )}
                onChange={() => {
                  onFacialExpressionCategoryClick(
                    'positive',
                    Object.values(FACE_CATEGORIES.positive.options).every((opt) =>
                      selectedOptions.some((selected) => selected.value === opt.value)
                    )
                  );
                }}
              />
            </div>
            {Object.keys(FACE_CATEGORIES.positive.options).map((optionKey, index) => {
              const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
              return (
                <div key={index} style={{ padding: '6px 20px' }}>
                  <CheckboxItem
                    label={optionKey}
                    imageComponent={<IconComponent />}
                    imageSize={32}
                    isChecked={selectedOptions.some(
                      (opt) =>
                        opt.value ===
                        FACE_CATEGORIES.positive.options[optionKey as keyof typeof FACE_CATEGORIES.positive.options]
                          ?.value
                    )}
                    onChange={() =>
                      onSelect(
                        FACE_CATEGORIES.positive.options[optionKey as keyof typeof FACE_CATEGORIES.positive.options]
                      )
                    }
                  />
                </div>
              );
            })}
          </div>

          {/* 중립 카테고리 */}
          <div style={{ flex: 1 }}>
            <div style={{ padding: '6px 20px', borderBottom: `1px solid ${colorSet.gray200}` }}>
              <CheckboxItem
                label="중립"
                isChecked={Object.values(FACE_CATEGORIES.neutral.options).every((opt) =>
                  selectedOptions.some((selected) => selected.value === opt.value)
                )}
                onChange={() => {
                  onFacialExpressionCategoryClick(
                    'neutral',
                    Object.values(FACE_CATEGORIES.neutral.options).every((opt) =>
                      selectedOptions.some((selected) => selected.value === opt.value)
                    )
                  );
                }}
              />
            </div>
            {Object.keys(FACE_CATEGORIES.neutral.options).map((optionKey, index) => {
              const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
              return (
                <div key={index} style={{ padding: '6px 20px' }}>
                  <CheckboxItem
                    label={optionKey}
                    imageComponent={<IconComponent />}
                    imageSize={32}
                    isChecked={selectedOptions.some(
                      (opt) =>
                        opt.value ===
                        FACE_CATEGORIES.neutral.options[optionKey as keyof typeof FACE_CATEGORIES.neutral.options]
                          ?.value
                    )}
                    onChange={() =>
                      onSelect(
                        FACE_CATEGORIES.neutral.options[optionKey as keyof typeof FACE_CATEGORIES.neutral.options]
                      )
                    }
                  />
                </div>
              );
            })}
          </div>

          {/* 부정 카테고리 */}
          <div style={{ flex: 1 }}>
            <div style={{ padding: '6px 20px', borderBottom: `1px solid ${colorSet.gray200}` }}>
              <CheckboxItem
                label="부정"
                isChecked={Object.values(FACE_CATEGORIES.negative.options).every((opt) =>
                  selectedOptions.some((selected) => selected.value === opt.value)
                )}
                onChange={() => {
                  onFacialExpressionCategoryClick(
                    'negative',
                    Object.values(FACE_CATEGORIES.negative.options).every((opt) =>
                      selectedOptions.some((selected) => selected.value === opt.value)
                    )
                  );
                }}
              />
            </div>
            {Object.keys(FACE_CATEGORIES.negative.options).map((optionKey, index) => {
              const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
              return (
                <div key={index} style={{ padding: '6px 20px' }}>
                  <CheckboxItem
                    label={optionKey}
                    imageComponent={<IconComponent />}
                    imageSize={32}
                    isChecked={selectedOptions.some(
                      (opt) =>
                        opt.value ===
                        FACE_CATEGORIES.negative.options[optionKey as keyof typeof FACE_CATEGORIES.negative.options]
                          ?.value
                    )}
                    onChange={() =>
                      onSelect(
                        FACE_CATEGORIES.negative.options[optionKey as keyof typeof FACE_CATEGORIES.negative.options]
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// XButton 컴포넌트
export const ChipXButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={(event) => {
      event.stopPropagation(); // 부모 요소의 클릭 이벤트가 실행되지 않도록 막음
      onClick();
    }}
    style={{
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 16
    }}
  >
    <img src={x_black} style={{ width: 14, height: 14 }} />
  </button>
);

// Chip 컴포넌트
interface IChipProps {
  label: string;
  needTooltip?: boolean;
  tooltipText?: string;
  paperType?: ChipPaperType;
  options: IOptionList;
  storedChildOptionValue: IOptionList | null;
  setSelectedOption: (param: IOptionList | null) => void;
  isError?: boolean;
  disabled?: boolean;
}

export const Chip: React.FC<IChipProps> = ({
  label,
  needTooltip = false,
  tooltipText = '',
  paperType = ChipPaperType.SELECT,
  options,
  storedChildOptionValue,
  setSelectedOption,
  isError = false,
  disabled = false
}) => {
  const [isActive, setIsActive] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);

  const selectedOptions = storedChildOptionValue
    ? Object.values(storedChildOptionValue).filter((option) => option.isSelected)
    : [];

  const handleChipClick = () => {
    setIsActive(!isActive);
  };

  const handleOptionSelect = (option: IOptionListItem) => {
    let updatedOptions: IOptionList | null;

    if (paperType === ChipPaperType.SELECT) {
      setIsActive(false);
      updatedOptions = Object.keys(options).reduce((acc, key) => {
        acc[key] = {
          ...options[key],
          isSelected:
            paperType === ChipPaperType.SELECT
              ? key === option.name
              : key === option.name
                ? !options[key].isSelected
                : options[key].isSelected
        };
        return acc;
      }, {} as IOptionList);
    } else if (paperType === ChipPaperType.CHECKBOX_LIST || paperType === ChipPaperType.FACE_CHECKBOX_LIST) {
      // 먼저 업데이트될 옵션 상태를 생성
      const tempUpdatedOptions = {
        ...storedChildOptionValue,
        [option.name]: {
          ...(storedChildOptionValue?.[option.name] ?? option),
          isSelected: !storedChildOptionValue?.[option.name]?.isSelected
        }
      };

      // 모든 체크박스가 uncheck되었는지 확인
      const allFalse = Object.values(tempUpdatedOptions).every((opt) => !opt.isSelected);

      // 모든 체크박스가 uncheck되었을 시 null 할당, 아니면 업데이트된 옵션 사용
      updatedOptions = allFalse ? null : tempUpdatedOptions;
    } else {
      updatedOptions = null;
    }

    setSelectedOption(updatedOptions);
  };

  const handleFacialExpressionCategoryClick = (
    category: 'positive' | 'neutral' | 'negative',
    isCategoryChecked: boolean
  ) => {
    const options = Object.values(FACE_CATEGORIES[category].options);

    const tempUpdatedOptions = { ...storedChildOptionValue };

    options.forEach((option) => {
      tempUpdatedOptions[option.name] = {
        ...(tempUpdatedOptions?.[option.name] ?? option),
        isSelected: !isCategoryChecked // isCategoryChecked가 false면 true로, true면 false로
      };
    });

    const allFalse = Object.values(tempUpdatedOptions).every((opt) => !opt.isSelected);
    const updatedOptions = allFalse ? null : tempUpdatedOptions;

    setSelectedOption(updatedOptions);
  };

  const handleResetChip = () => {
    setIsActive(false);
    setSelectedOption(null);
  };

  //Chip 바깥 영역 클릭시 페이퍼 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chipRef.current && !chipRef.current.contains(event.target as Node)) {
        setIsActive(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={chipRef} style={{ position: 'relative', display: 'inline-flex', whiteSpace: 'nowrap' }}>
      {selectedOptions.length === 0 ? (
        <div
          style={{
            height: 38,
            padding: '9px 16px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${isActive ? colorSet.primaryMain : isError ? colorSet.errorMain : colorSet.gray300}`,
            backgroundColor: disabled ? colorSet.gray200 : isActive ? 'rgba(119, 78, 255, 0.15)' : 'transparent',
            cursor: disabled ? 'default' : 'pointer'
          }}
          onClick={() => {
            if (disabled) return;
            handleChipClick();
          }}
        >
          {needTooltip && <Tooltip text={tooltipText} />}
          <Text variant="body1" color={disabled ? colorSet.gray400 : colorSet.gray600}>
            {label}
          </Text>
        </div>
      ) : (
        <div
          style={{
            height: 38,
            padding: '9px 16px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${isActive ? colorSet.primaryMain : colorSet.gray600}`,
            backgroundColor: isActive ? 'rgba(119, 78, 255, 0.15)' : colorSet.gray200,
            cursor: 'pointer'
          }}
          onClick={handleChipClick}
        >
          {needTooltip && <Tooltip text={tooltipText} />}
          <Text variant="body1" fontWeight="bold" style={{ marginRight: 8 }}>
            {label}
          </Text>
          <Text variant="body1">
            {selectedOptions
              .slice(0, 2)
              .map((option) => option.name)
              .join(', ')}
          </Text>
          {selectedOptions.length > 2 ? (
            <Text variant="body1" color={colorSet.gray400}>
              &nbsp;{`+${selectedOptions.length - 2} more`}
            </Text>
          ) : null}
          <ChipXButton onClick={handleResetChip} />
        </div>
      )}
      {isActive && (
        <PaperSelect
          options={options}
          selectedOptions={selectedOptions}
          onSelect={handleOptionSelect}
          paperType={paperType}
          onFacialExpressionCategoryClick={handleFacialExpressionCategoryClick}
        />
      )}
    </div>
  );
};
